import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import CodeIcon from '@mui/icons-material/Code';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BlockIcon from '@mui/icons-material/Block';
import RestoreIcon from '@mui/icons-material/Restore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { ServiceMetrics } from './types';
import { parseDateRangeFromParams } from './DateRangePicker';
import { ExcludeRepoDialog } from './ExcludeRepoDialog';
import { CoverageProgressBar } from './code';
import { UIRepoWithTypedCoverage } from '../../../../../../api/teamMetricsApi';

type SortKey = 'name' | 'team' | 'linesOfCode' | 'coverage' | 'trend' | 'prCount' | 'deploys';
type SortDirection = 'asc' | 'desc';

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(4),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  legend: {
    display: 'flex',
    gap: theme.spacing(4),
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
  },
  legendText: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  tableContainer: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#F7FAFC',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderBottom: '1px solid #E2E8F0',
  },
  sortLabel: {
    '&.MuiTableSortLabel-root': {
      color: '#8E9299',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    '&.MuiTableSortLabel-root:hover': {
      color: '#4A5568',
    },
    '&.Mui-active': {
      color: '#1A1A1A',
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#8E9299',
      fontSize: 14,
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#1A1A1A',
    },
  },
  tableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
    '&:hover $actionButton': {
      opacity: 1,
    },
  },
  healthyRow: {
    backgroundColor: 'rgba(56, 161, 105, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(56, 161, 105, 0.1)',
    },
  },
  warningRow: {
    backgroundColor: 'rgba(214, 158, 46, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(214, 158, 46, 0.1)',
    },
  },
  criticalRow: {
    backgroundColor: 'rgba(229, 62, 62, 0.05)',
    '&:hover': {
      backgroundColor: 'rgba(229, 62, 62, 0.1)',
    },
  },
  excludedRow: {
    backgroundColor: '#EDF2F7',
    borderLeft: '3px solid #A0AEC0',
    '&:hover': {
      backgroundColor: '#E2E8F0',
    },
    '&:hover $actionButton': {
      opacity: 1,
    },
    '& $serviceIcon': {
      color: '#A0AEC0',
    },
  },
  excludedText: {
    fontFamily: 'monospace',
    fontSize: 14,
    color: '#A0AEC0',
  },
  excludedLink: {
    fontSize: 14,
    fontWeight: 700,
    color: '#A0AEC0',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  excludedBadge: {
    backgroundColor: '#EDF2F7',
    color: '#718096',
    fontSize: 9,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 4,
    marginLeft: 8,
    textTransform: 'uppercase',
    cursor: 'help',
  },
  actionButton: {
    padding: 4,
    opacity: 0,
    transition: 'opacity 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  actionButtonVisible: {
    opacity: 1,
  },
  actionButtonDisabled: {
    padding: 4,
    opacity: 0.4,
    color: '#A0AEC0',
    cursor: 'default',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  toggleButtonGroup: {
    '& .MuiToggleButton-root': {
      padding: '4px 12px',
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'none',
      border: '1px solid #E2E8F0',
      color: '#4A5568',
      '&.Mui-selected': {
        backgroundColor: '#EDF2F7',
        color: '#1A1A1A',
        '&:hover': {
          backgroundColor: '#E2E8F0',
        },
      },
      '&:hover': {
        backgroundColor: '#F7FAFC',
      },
    },
  },
  filterCount: {
    fontSize: 12,
    color: '#8E9299',
  },
  cell: {
    borderBottom: '1px solid #E2E8F0',
  },
  serviceCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  serviceIcon: {
    fontSize: 16,
    color: '#4A5568',
  },
  serviceLink: {
    fontSize: 14,
    fontWeight: 700,
    color: '#0070F3',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  monoText: {
    fontFamily: 'monospace',
    fontSize: 14,
    color: '#4A5568',
  },
  coverageHealthy: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    color: '#22543D',
  },
  coverageWarning: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    color: '#744210',
  },
  coverageCritical: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 700,
    color: '#C53030',
  },
  coverageUnknown: {
    fontFamily: 'monospace',
    fontSize: 14,
    fontWeight: 500,
    color: '#A0AEC0',
    fontStyle: 'italic',
  },
  trendCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
  },
  trendPositive: {
    fontSize: 12,
    fontWeight: 500,
    color: '#38A169',
  },
  trendNegative: {
    fontSize: 12,
    fontWeight: 500,
    color: '#E53E3E',
  },
  trendIcon: {
    fontSize: 14,
  },
}));

interface ServiceBreakdownTableProps {
  services?: ServiceMetrics[];
  repoSummaries?: UIRepoWithTypedCoverage[];
  onExcludeRepo: (repoName: string, reason: string) => Promise<void>;
  onIncludeRepo: (repoName: string) => Promise<void>;
  isExcluding: boolean;
  showTeamColumn?: boolean;
}

export const ServiceBreakdownTable: React.FC<ServiceBreakdownTableProps> = ({
  services,
  repoSummaries,
  onExcludeRepo,
  onIncludeRepo,
  isExcluding,
  showTeamColumn = false,
}) => {
  // Determine if we're using new typed coverage or legacy mode
  const useTypedCoverage = repoSummaries && repoSummaries.length > 0;

  // Normalize data to a common format for rendering
  const normalizedData = useMemo(() => {
    if (useTypedCoverage && repoSummaries) {
      return repoSummaries.map((repo) => {
        const combinedCoverage = repo.coverage.find((c) => c.testType === 'go_combined');
        return {
          name: repo.name,
          owningTeam: repo.owningTeam,
          coverage: combinedCoverage?.coveragePercentage ?? null,
          trend: combinedCoverage?.coverageDelta != null
            ? (combinedCoverage.coverageDelta >= 0 ? '+' : '') + combinedCoverage.coverageDelta.toFixed(2) + '%'
            : null,
          status: getCoverageStatus(combinedCoverage?.coveragePercentage ?? null),
          deploys: repo.deploymentCount,
          linesOfCode: repo.totalUnits,
          prCount: repo.prCount,
          color: repo.color,
          isExcluded: repo.isExcluded,
          exclusionReason: repo.exclusionReason,
          excludedByEmail: repo.excludedByEmail,
          excludedAt: repo.excludedAt,
          typedCoverage: repo.coverage,
        };
      });
    }
    // Legacy mode
    return (services ?? []).map((s) => ({
      ...s,
      owningTeam: '',
      excludedByEmail: null,
      excludedAt: null,
      typedCoverage: undefined,
    }));
  }, [useTypedCoverage, repoSummaries, services]);

  function getCoverageStatus(coverage: number | null): 'healthy' | 'warning' | 'critical' | 'unknown' {
    if (coverage == null) return 'unknown';
    if (coverage >= 50) return 'healthy';
    if (coverage >= 25) return 'warning';
    return 'critical';
  }

  const classes = useStyles();
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'excluded'>('all');

  const handleFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newFilter: 'all' | 'excluded' | null,
  ) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const excludedCount = useMemo(() => normalizedData.filter((s) => s.isExcluded).length, [normalizedData]);

  const dateRangeParams = useMemo(() => {
    const dateRange = parseDateRangeFromParams(new URLSearchParams(window.location.search));
    return `?startDate=${dateRange.start}`;
  }, []);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortKey(null);
        setSortDirection('desc');
      }
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedServices = useMemo(() => {
    if (!sortKey) {
      return normalizedData;
    }

    return [...normalizedData].sort((a, b) => {
      let comparison = 0;
      switch (sortKey) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'team':
          comparison = (a.owningTeam || '').localeCompare(b.owningTeam || '');
          break;
        case 'linesOfCode':
          if (a.linesOfCode === null && b.linesOfCode === null) comparison = 0;
          else if (a.linesOfCode === null) comparison = -1;
          else if (b.linesOfCode === null) comparison = 1;
          else comparison = a.linesOfCode - b.linesOfCode;
          break;
        case 'coverage':
          if (a.coverage === null && b.coverage === null) comparison = 0;
          else if (a.coverage === null) comparison = -1;
          else if (b.coverage === null) comparison = 1;
          else comparison = a.coverage - b.coverage;
          break;
        case 'trend':
          if (a.trend === null && b.trend === null) comparison = 0;
          else if (a.trend === null) comparison = -1;
          else if (b.trend === null) comparison = 1;
          else {
            const aTrend = parseFloat(a.trend.replace('%', '').replace('+', ''));
            const bTrend = parseFloat(b.trend.replace('%', '').replace('+', ''));
            comparison = aTrend - bTrend;
          }
          break;
        case 'prCount':
          comparison = a.prCount - b.prCount;
          break;
        case 'deploys':
          comparison = a.deploys - b.deploys;
          break;
        default:
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [normalizedData, sortKey, sortDirection]);

  const filteredServices = useMemo(() => {
    if (filter === 'excluded') {
      return sortedServices.filter((s) => s.isExcluded);
    }
    return sortedServices;
  }, [sortedServices, filter]);

  type NormalizedService = typeof normalizedData[number];

  const getRowClass = (service: NormalizedService) => {
    if (service.isExcluded) {
      return classes.excludedRow;
    }
    switch (service.status) {
      case 'healthy':
        return classes.healthyRow;
      case 'warning':
        return classes.warningRow;
      case 'critical':
        return classes.criticalRow;
      case 'unknown':
      default:
        return '';
    }
  };

  const handleOpenExcludeDialog = (repoName: string) => {
    setSelectedRepo(repoName);
    setExcludeDialogOpen(true);
  };

  const handleCloseExcludeDialog = () => {
    setExcludeDialogOpen(false);
    setSelectedRepo(null);
  };

  const handleExclude = async (reason: string) => {
    if (selectedRepo) {
      await onExcludeRepo(selectedRepo, reason);
      handleCloseExcludeDialog();
    }
  };

  const getCoverageClass = (status: string) => {
    switch (status) {
      case 'healthy':
        return classes.coverageHealthy;
      case 'warning':
        return classes.coverageWarning;
      case 'critical':
        return classes.coverageCritical;
      case 'unknown':
        return classes.coverageUnknown;
      default:
        return classes.monoText;
    }
  };

  const getLinesOfCodeClass = (service: NormalizedService) => {
    if (service.isExcluded) return classes.excludedText;
    if (service.linesOfCode === null) return classes.coverageUnknown;
    return classes.monoText;
  };

  const getTrendClass = (service: NormalizedService) => {
    if (service.isExcluded) return classes.excludedText;
    return classes.coverageUnknown;
  };

  const renderTrendCell = (service: NormalizedService) => {
    if (service.trend === null) {
      return <Typography className={getTrendClass(service)}>N/A</Typography>;
    }
    if (service.isExcluded) {
      return <Typography className={classes.excludedText}>{service.trend}</Typography>;
    }
    const isPositive = service.trend.startsWith('+');
    return (
      <Box className={classes.trendCell}>
        {isPositive ? (
          <TrendingUpIcon className={`${classes.trendIcon} ${classes.trendPositive}`} />
        ) : (
          <TrendingDownIcon className={`${classes.trendIcon} ${classes.trendNegative}`} />
        )}
        <Typography className={isPositive ? classes.trendPositive : classes.trendNegative}>
          {service.trend}
        </Typography>
      </Box>
    );
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Service Breakdown</Typography>
        <Box className={classes.legend}>
          {useTypedCoverage ? (
            <>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: '#3182CE' }} />
                <Typography className={classes.legendText}>Unit</Typography>
              </Box>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: '#38A169' }} />
                <Typography className={classes.legendText}>TAS</Typography>
              </Box>
              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendDot}
                  style={{
                    backgroundColor: '#A0AEC0',
                    background:
                      'repeating-linear-gradient(45deg, #A0AEC0, #A0AEC0 2px, #CBD5E0 2px, #CBD5E0 4px)',
                  }}
                />
                <Typography className={classes.legendText}>Excluded</Typography>
              </Box>
            </>
          ) : (
            <>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: '#38A169' }} />
                <Typography className={classes.legendText}>Healthy</Typography>
              </Box>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: '#D69E2E' }} />
                <Typography className={classes.legendText}>Warning</Typography>
              </Box>
              <Box className={classes.legendItem}>
                <Box className={classes.legendDot} style={{ backgroundColor: '#E53E3E' }} />
                <Typography className={classes.legendText}>Critical</Typography>
              </Box>
              <Box className={classes.legendItem}>
                <Box
                  className={classes.legendDot}
                  style={{
                    backgroundColor: '#A0AEC0',
                    background:
                      'repeating-linear-gradient(45deg, #A0AEC0, #A0AEC0 2px, #CBD5E0 2px, #CBD5E0 4px)',
                  }}
                />
                <Typography className={classes.legendText}>Excluded</Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box className={classes.filterRow}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={handleFilterChange}
          size="small"
          className={classes.toggleButtonGroup}
        >
          <ToggleButton value="all">Show All ({normalizedData.length})</ToggleButton>
          <ToggleButton value="excluded" disabled={excludedCount === 0}>
            Excluded Only ({excludedCount})
          </ToggleButton>
        </ToggleButtonGroup>
        {filter === 'excluded' && excludedCount > 0 && (
          <Typography className={classes.filterCount}>
            Showing {excludedCount} excluded {excludedCount === 1 ? 'repository' : 'repositories'}
          </Typography>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.headerCell}>
                <TableSortLabel
                  active={sortKey === 'name'}
                  direction={sortKey === 'name' ? sortDirection : 'desc'}
                  onClick={() => handleSort('name')}
                  className={classes.sortLabel}
                >
                  Service / Repository
                </TableSortLabel>
              </TableCell>
              {showTeamColumn && (
                <TableCell className={classes.headerCell}>
                  <TableSortLabel
                    active={sortKey === 'team'}
                    direction={sortKey === 'team' ? sortDirection : 'desc'}
                    onClick={() => handleSort('team')}
                    className={classes.sortLabel}
                  >
                    Team
                  </TableSortLabel>
                </TableCell>
              )}
              <TableCell className={classes.headerCell} align="right">
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
                  <Tooltip title="For Go repos, this is the number of statements" placement="top">
                    <InfoOutlinedIcon style={{ fontSize: 14, color: '#A0AEC0', cursor: 'help' }} />
                  </Tooltip>
                  <TableSortLabel
                    active={sortKey === 'linesOfCode'}
                    direction={sortKey === 'linesOfCode' ? sortDirection : 'desc'}
                    onClick={() => handleSort('linesOfCode')}
                    className={classes.sortLabel}
                  >
                    Size
                  </TableSortLabel>
                </Box>
              </TableCell>
              <TableCell className={classes.headerCell} style={{ minWidth: 220 }}>
                <TableSortLabel
                  active={sortKey === 'coverage'}
                  direction={sortKey === 'coverage' ? sortDirection : 'desc'}
                  onClick={() => handleSort('coverage')}
                  className={classes.sortLabel}
                >
                  Test Coverage
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.headerCell} align="right">
                <TableSortLabel
                  active={sortKey === 'trend'}
                  direction={sortKey === 'trend' ? sortDirection : 'desc'}
                  onClick={() => handleSort('trend')}
                  className={classes.sortLabel}
                >
                  Trend
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.headerCell} align="right">
                <TableSortLabel
                  active={sortKey === 'prCount'}
                  direction={sortKey === 'prCount' ? sortDirection : 'desc'}
                  onClick={() => handleSort('prCount')}
                  className={classes.sortLabel}
                >
                  PRs
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.headerCell} align="right">
                <TableSortLabel
                  active={sortKey === 'deploys'}
                  direction={sortKey === 'deploys' ? sortDirection : 'desc'}
                  onClick={() => handleSort('deploys')}
                  className={classes.sortLabel}
                >
                  Deploys
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.headerCell} width={40} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredServices.map((service) => (
              <TableRow
                key={service.name}
                className={`${classes.tableRow} ${getRowClass(service)}`}
              >
                <TableCell className={classes.cell}>
                  <Box className={classes.serviceCell}>
                    <CodeIcon className={classes.serviceIcon} />
                    <Link
                      to={`/catalog/default/component/${service.name}/health${dateRangeParams}`}
                      className={service.isExcluded ? classes.excludedLink : classes.serviceLink}
                    >
                      {service.name}
                    </Link>
                    {service.isExcluded && (
                      <Tooltip
                        title={
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              Excluded from calculations
                            </Typography>
                            {service.exclusionReason && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                Reason: {service.exclusionReason}
                              </Typography>
                            )}
                            {service.excludedByEmail && (
                              <Typography variant="body2" sx={{ mt: 1 }}>
                                Excluded by: {service.excludedByEmail}
                              </Typography>
                            )}
                            {service.excludedAt && (
                              <Typography variant="body2">
                                Date: {new Date(service.excludedAt).toLocaleDateString()}
                              </Typography>
                            )}
                          </Box>
                        }
                      >
                        <span className={classes.excludedBadge}>Excluded</span>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                {showTeamColumn && (
                  <TableCell className={classes.cell}>
                    <Typography className={service.isExcluded ? classes.excludedText : classes.monoText}>
                      {service.owningTeam ? service.owningTeam.replace('acl-dev-team-', '') : '-'}
                    </Typography>
                  </TableCell>
                )}
                <TableCell className={classes.cell} align="right">
                  <Typography className={getLinesOfCodeClass(service)}>
                    {service.linesOfCode === null ? 'N/A' : service.linesOfCode.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell className={classes.cell}>
                  {service.typedCoverage && service.typedCoverage.length > 0 ? (
                    <CoverageProgressBar coverage={service.typedCoverage} />
                  ) : (
                    <Typography
                      className={service.isExcluded ? classes.excludedText : getCoverageClass(service.status)}
                    >
                      {service.coverage === null ? 'N/A' : `${service.coverage}%`}
                    </Typography>
                  )}
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  {renderTrendCell(service)}
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  <Typography className={service.isExcluded ? classes.excludedText : classes.monoText}>
                    {service.prCount}
                  </Typography>
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  <Typography className={service.isExcluded ? classes.excludedText : classes.monoText}>
                    {service.deploys}
                  </Typography>
                </TableCell>
                <TableCell className={classes.cell} align="right">
                  {service.isExcluded ? (
                    <Tooltip title="Include in calculations">
                      <IconButton
                        className={`${classes.actionButton} ${classes.actionButtonVisible}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onIncludeRepo(service.name);
                        }}
                        size="small"
                      >
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : service.coverage === null ? (
                    <Tooltip title="No test coverage data for this repo — already excluded from coverage calculations">
                      <span>
                        <IconButton
                          className={classes.actionButtonDisabled}
                          disabled
                          size="small"
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Exclude from calculations">
                      <IconButton
                        className={classes.actionButton}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleOpenExcludeDialog(service.name);
                        }}
                        size="small"
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <ExcludeRepoDialog
        open={excludeDialogOpen}
        repoName={selectedRepo || ''}
        onClose={handleCloseExcludeDialog}
        onExclude={handleExclude}
        isLoading={isExcluding}
      />
    </Box>
  );
};

export default ServiceBreakdownTable;
